<template>
    <div id="calculateur-beta" class="calc">
        <score-bar :name="recipe.name || ''" :save-state="autoStatus" :save-state-text="saveStateText" :result="resultCalcul"
                   :user-role="userRole" :is-domain="isDomain" :text-link-abo-pro="textLinkAboPro" :url-link-abo-pro="urlLinkAboPro"
                   :can-see-letters="canSeeDetails" @update:name="setName" @retry="retry"></score-bar>

        <div class="errors" v-if="errors.length">
            <div class="box" role="alert">
                <strong>Des informations manquent :</strong>
                <ul><li v-for="e in errors" :key="e.anchor"><button type="button" class="link-btn" @click="goTo(e.anchor)">{{ e.message }}</button></li></ul>
            </div>
        </div>

        <div class="calc-wrap">
            <!-- Placeholders pendant le chargement de la recette -->
            <div v-if="loading" class="skel-page" aria-busy="true" aria-label="Chargement de la recette">
                <div class="settings"><div class="setbox skel-box"><span class="skel w-30"></span></div><div class="setbox skel-box"><span class="skel w-60"></span></div></div>
                <div class="calc-grid">
                    <div class="calc-main">
                        <section class="ccard"><div class="ccard-head"><span class="skel w-30"></span></div><div class="ccard-body">
                            <span class="skel skel-field"></span>
                            <div class="rows"><div v-for="k in 4" :key="k" class="row-i skel-row"><span class="skel w-40"></span><span class="skel w-60 thin"></span></div></div>
                        </div></section>
                    </div>
                    <aside class="calc-side"><section class="ccard"><div class="ccard-head"><span class="skel w-40"></span></div><div class="ccard-body"><span class="skel w-80"></span><span class="skel w-60"></span><span class="skel w-70"></span></div></section></aside>
                </div>
            </div>
            <template v-else>
            <recipe-settings :nb-couvert="recipe.nb_couvert" :rendement="recipe.rendement" :result="resultCalcul"
                             @update:nbCouvert="recipe.nb_couvert = $event" @update:rendement="recipe.rendement = $event"></recipe-settings>

            <div class="manual-actions" v-if="!autoCalculAvailable">
                <button v-if="!userRole" type="button" class="primary-btn" @click="displayPopupAbo()">Calculer les impacts</button>
                <template v-else>
                    <button type="button" class="primary-btn" @click="calcul()">Calculer les impacts</button>
                    <button type="button" class="ghost-btn" v-loading="loadingSave" :disabled="loadingSave" @click="save">Enregistrer</button>
                </template>
            </div>

            <div class="calc-grid">
                <div class="calc-main">
                    <collapsible-card id="card-details" title="Caractéristiques et actions" :open.sync="openDetails" v-if="userRole">
                        <recipe-details-card :recipe="recipe" :user-role="userRole" :csrf="csrf"
                                             :options="{categories: optionsCategories, subcategories: optionsSubcategories, budget: optionsBudget, minutes: optionsMinutes, facilite: optionsFacilite}"
                                             @transform="openDialogTransform" @choose-picture="choiceFile"></recipe-details-card>
                        <input @change="handleFileUpload" ref="picture" name="picture" type="file" id="picture" class="sr-only" aria-label="Photo de la recette">
                    </collapsible-card>

                    <section class="ccard">
                        <div class="ccard-head">
                            <img src="/images/recette-bref.svg" alt="" class="masc">
                            <h2 class="ccard-title">Composition</h2>
                            <span class="eyebrow" style="margin:0">{{ flatIngredients.length }} ingrédient{{ flatIngredients.length > 1 ? 's' : '' }}</span>
                        </div>
                        <div class="ccard-body">
                            <ingredient-search :types="types" :search="searchIngredients" @select="addIngredient"></ingredient-search>
                            <div v-if="!flatIngredients.length" class="empty"><img src="/images/recette-bref.svg" alt="" class="masc-xl">Aucun ingrédient. Recherchez-en un ci-dessus.</div>
                            <div class="rows" v-else>
                                <ingredient-row v-for="row in flatIngredients" :key="row.ingredient.uniqid"
                                                :ingredient="row.ingredient" :type="row.type"
                                                :row-id="'row-' + row.ingredient.uniqid"
                                                :expanded="!!expandedRows[row.ingredient.uniqid]"
                                                :touched="touchedRows[row.ingredient.uniqid] !== false"
                                                :dominant="row.ingredient.uniqid === dominantUniqid"
                                                :total-result="totalResult"
                                                :weights="weightsByUniqid[row.ingredient.uniqid] || null"
                                                :rendement-label="rendementLabel"
                                                :error="rowErrors[row.ingredient.uniqid] || null"
                                                @update="updateIngredient(row, $event)"
                                                @toggle="toggleRow(row.ingredient.uniqid)"
                                                @remove="removeIngredient(row)"
                                                @quantity-change="scheduleGco2e(row)"></ingredient-row>
                            </div>
                        </div>
                    </section>

                    <collapsible-card id="card-after" title="Après la recette" :open.sync="openAfter" mascot="/images/ingredients.svg"
                                      subtitle="Transformation, emballage, distribution et remise en température. Ces étapes complètent les ingrédients pour former le coût environnemental.">
                        <after-recipe-card :recipe="recipe" :user-role="userRole" :package-limit="packageLimit" :delivery-limit="deliveryLimit" :url-link-abo-pro="urlLinkAboPro"
                                           :options="{packageTypes: optionsPackageTypes, ecobalysePackagings: optionsEcobalysePackagings, deliveryModes: optionsDeliveryModes, preparations: optionsPreparations, transformations: optionsTransformations}"></after-recipe-card>
                    </collapsible-card>

                    <collapsible-card id="card-nutrition" title="Valeurs nutritionnelles" :open.sync="openNutrition" v-if="resultCalcul">
                        <nutrition-card :result="resultCalcul" :can-see-details="canSeeDetails" :url-link-abo-pro="urlLinkAboPro" :text-link-abo-pro="textLinkAboPro"></nutrition-card>
                    </collapsible-card>
                </div>

                <aside class="calc-side">
                    <improvement-tips-card v-if="flatIngredients.length" :tips="visibleTips" :history-count="tipsHistory.length" :busy="applyingTip"
                                           @apply="applyTip" @undo="undoTip" @reset="resetTips" />

                    <collapsible-card id="card-impact" title="Détail de l'impact" :open.sync="openImpact" mascot="/images/impact-de-ma-recette.svg" v-if="resultCalcul">
                        <impact-detail-card :result="resultCalcul" :user-role="userRole" :can-see-details="canSeeDetails" :url-link-abo-pro="urlLinkAboPro" :text-link-abo-pro="textLinkAboPro"></impact-detail-card>
                    </collapsible-card>

                    <div v-if="resultCalcul && userRole == 'admin'" class="dev-toggle">
                        <button type="button" class="link-btn" :aria-expanded="devMode ? 'true' : 'false'" @click="devMode = !devMode">Mode développeur</button>
                        <div v-if="devMode" class="ccard mt-2" style="padding:14px">
                            <div>
                                <small><a href="#" @click.prevent="detailsCalcul = !detailsCalcul"><span v-if="detailsCalcul">Masquer</span><span
                                    v-else>Afficher</span> les détails de calcul</a></small>
                                <small v-if="recipe.id" style="margin-left: 10px;">
                                    <a :href="'/calculateur/' + recipe.id + '/json'" target="_blank">Voir le JSON du calcul</a>
                                </small>
                            </div>
                            <template v-if="detailsCalcul">
                                Score Environnemental :
                                <ul>
                                    <li v-for="(value, name) in resultCalcul.singlescore" :key="name">{{ name }} : {{ value }}</li>
                                </ul>
                                Climat-score :
                                <ul>
                                    <li v-for="(value, name) in resultCalcul.climatscore" :key="name">{{ name }} : {{ value }}</li>
                                </ul>
                                Datas :
                                <json-viewer :value="resultCalcul"></json-viewer>
                            </template>
                        </div>
                    </div>
                </aside>
            </div>
            </template>
        </div>

        <el-dialog title="Transformer en ingrédient" :visible.sync="dialogueTransform" width="50%"
                   :close-on-click-modal="false" :close-on-press-escape="false" @closed="closedModalDialog">
            <p>Transformer la recette en ingrédient ?</p>
            <el-alert v-if="transformStatus.already_exists"
                      title="L'ingrédient existe déjà. Vous pouvez soit l'ajouter en plus de l'existant ou écraser l'existant."
                      type="warning"></el-alert>
            <span slot="footer" class="dialog-footer">
                <el-button @click="dialogueTransform = false">Annuler</el-button>
                <template v-if="transformStatus.already_exists">
                    <el-button :loading="loadingTransform" @click.prevent="transformToIngredient('overide')"
                               type="warning">Écraser</el-button>
                    <el-button :loading="loadingTransform" @click.prevent="transformToIngredient('add')" type="primary">Ajouter</el-button>
                </template>
                <template v-if="!transformStatus.already_exists">
                    <el-button :loading="loadingTransform" @click.prevent="transformToIngredient('add')" type="primary">Confirmer</el-button>
                </template>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import JsonViewer from 'vue-json-viewer'
import ScoreBar from './Calculateur/ScoreBar.vue'
import CollapsibleCard from './Calculateur/CollapsibleCard.vue'
import RecipeSettings from './Calculateur/RecipeSettings.vue'
import IngredientSearch from './Calculateur/IngredientSearch.vue'
import IngredientRow from './Calculateur/IngredientRow.vue'
import RecipeDetailsCard from './Calculateur/RecipeDetailsCard.vue'
import AfterRecipeCard from './Calculateur/AfterRecipeCard.vue'
import NutritionCard from './Calculateur/NutritionCard.vue'
import ImpactDetailCard from './Calculateur/ImpactDetailCard.vue'
import ImprovementTipsCard from './Calculateur/ImprovementTipsCard.vue'

export default {
    components: {JsonViewer, ScoreBar, CollapsibleCard, RecipeSettings, IngredientSearch, IngredientRow, RecipeDetailsCard, AfterRecipeCard, NutritionCard, ImpactDetailCard, ImprovementTipsCard},
    data() {
        return {
            types: [],
            roleCanTransform: ['admin', 'pro+'],
            dialogueTransform: false,
            loadingSave: false,
            group: null,
            detailsCalcul: false,
            // Écran : erreurs nommées, état déplié / « réglages par défaut » de chaque ligne, bloc dev, cartes
            loading: true,
            errors: [],
            rowErrors: {},
            expandedRows: {},
            touchedRows: {},
            devMode: false,
            openDetails: false,
            openAfter: true,
            openNutrition: true,
            openImpact: true,
            csrf: (document.head.querySelector('meta[name="csrf-token"]') || {}).content || '',
            recipeToIngredient: {
                type_id: null
            },
            transformStatus: {
                already_exists: false
            },
            loadingTransform: false,
            rules: {},
            optionsPackageTypes: [],
            optionsEcobalysePackagings: [],
            optionsDeliveryModes: [],
            optionsPreparations: {},
            optionsTransformations: {},
            // Calcul dynamique : état affiché, empreinte des entrées du dernier calcul, séquence des requêtes
            autoStatus: null,
            autoSavedAt: null,
            autoRunning: false,
            autoPending: false,
            lastCalculFingerprint: null,
            calculSeq: 0,
            optionsCategories: [],
            optionsSubcategories: [],
            optionsMinutes: [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95],
            optionsFacilite: [{label: 'Facile', value: 1}, {label: 'Moyen', value: 2}, {label: 'Dur', value: 3}],
            optionsBudget: [{label: '€', value: 'EUR'}, {label: '€€', value: 'EUREUR'}, {
                label: '€€€',
                value: 'EUREUREUR'
            },],
            resultCalcul: {
                calcul: {},
                singlescore: {},
                climatscore: {}
            },
            packageLimit: 5,
            deliveryLimit: 3,
            recipe: {
                category_id: null,
                subcategory_id: null,
                subcategories_ids: [],
                preparation_minutes: null,
                cuisson_minutes: null,
                facilite: null,
                budget: null,
                name: null,
                text: null,
                video: null,
                scope_recipe: 'owner',
                nb_couvert: 1,
                rendement: null,
                picture: null,
                picture_url: null,
                package: false,
                packages: [
                    {
                        package_type_id: null,
                        package_weight_g: null
                    }
                ],
                ecobalyse_packagings: [
                    {
                        ecobalyse_packaging_id: null,
                        quantity: null
                    }
                ],
                delivery: false,
                deliveries: [
                    {
                        delivery_mode_id: null,
                        delivery_distance: null
                    }
                ],
                preparations: [],
                transformation: null,
            },
            urlAboPro: 'https://etiquettable.eco2initiative.com/abonnements/',
            // Pistes d'amélioration : liste courante, pile d'états avant chaque piste appliquée, empreinte des lignes après la dernière
            tips: [],
            tipsHistory: [],
            // Pile parallèle à tipsHistory : id de la piste appliquée à chaque étape (masquée tant que l'historique n'est pas vide)
            appliedTipIds: [],
            tipsBaseline: null,
            applyingTip: false,
            // Anti-doublon des requêtes /calculateur/pistes : empreinte des lignes déjà envoyées, numéro de séquence des réponses
            lastTipsFingerprint: null,
            tipsRequestSeq: 0,
        }
    },
    props: {
        recipeId: {
            default: null
        },
        userRole: {
            default: null
        },
        isDomain: {
            default: false
        },
    },
    computed: {
        textLinkAboPro() {
            return this.group ? 'adhérents ' + this.group.name : 'abonnements pro';
        },
        urlLinkAboPro() {
            return this.group && this.group.website ? this.group.website : 'https://etiquettable.eco2initiative.com/abonnements/';
        },
        // Rappel du mode de saisie des poids (cf. select recipe.rendement) sous chaque quantité
        rendementLabel() {
            if (this.recipe.rendement === 1) return 'brute';
            if (this.recipe.rendement === 0) return 'nette';
            return null;
        },
        // Calcul dynamique réservé aux rôles qui peuvent déjà calculer (les `user` gardent le popup abonnement)
        autoCalculAvailable() {
            return ['admin', 'pro', 'pro+'].indexOf(this.userRole) !== -1;
        },
        // Lettres, notes détaillées, nutrition et détail de l'impact : admin et pro+ uniquement
        canSeeDetails() {
            return this.userRole === 'admin' || this.userRole === 'pro+';
        },
        saveStateText() {
            if (!this.autoCalculAvailable) return '';
            if (this.autoStatus == 'calcul') return 'Calcul…';
            if (this.autoStatus == 'save') return 'Enregistrement…';
            if (this.autoStatus == 'error') return 'Échec de l\'enregistrement.';
            if (this.autoStatus == 'saved' && this.autoSavedAt) return 'Enregistré à ' + this.autoSavedAt;
            if (this.autoStatus == 'calculated') return this.recipe.id ? 'Résultats à jour' : 'Sera enregistré dès qu\'un titre est saisi';
            return this.recipe.id ? 'Enregistré' : 'Nouvelle recette';
        },
        // Toutes les lignes d'ingrédients, tous types confondus, avec leurs index d'origine
        // Liste plate dans l'ordre de saisie (spec §2.5 : une ligne ajoutée va en fin de liste), indépendamment du
        // regroupement par type du modèle de données ; `position` vient du serveur pour les lignes chargées
        flatIngredients() {
            const out = [];
            this.types.forEach((type, typeIndex) => {
                (type.ingredients || []).forEach((ingredient, ingredientIndex) => {
                    if (ingredient && ingredient.id) out.push({type, typeIndex, ingredient, ingredientIndex});
                });
            });
            return out.sort((a, b) => (a.ingredient.position || 0) - (b.ingredient.position || 0));
        },
        totalResult() {
            return this.flatIngredients.reduce((a, r) => a + (parseFloat(r.ingredient.result) || 0), 0);
        },
        dominantUniqid() {
            let best = null, max = 0;
            this.flatIngredients.forEach((r) => {
                const v = parseFloat(r.ingredient.result) || 0;
                if (v > max) { max = v; best = r.ingredient.uniqid; }
            });
            return best;
        },
        // Poids par ligne renvoyés par /calculateur/calcul dans calcul.ingredients[type_slug].list[]
        weightsByUniqid() {
            const map = {};
            const ings = this.resultCalcul && this.resultCalcul.ingredients ? this.resultCalcul.ingredients : {};
            Object.keys(ings).forEach((slug) => {
                (ings[slug] && ings[slug].list ? ings[slug].list : []).forEach((l) => {
                    if (l && l.uniqid) map[l.uniqid] = {poids_raw: l.poids_raw, poids_comestible: l.poids_comestible, poids: l.poids};
                });
            });
            return map;
        },
        // Une piste appliquée disparaît de la liste tant que l'historique n'est pas vide (F1)
        visibleTips() {
            return this.tips.filter((t) => this.appliedTipIds.indexOf(t.id) === -1);
        },
    },
    watch: {
        types: {
            deep: true,
            handler() {
                this.onInputChange();
                this.checkTipsHistory();
                // Ne relancer la requête pistes que si les lignes envoyées ont réellement changé depuis le dernier envoi
                if (this.scheduleTips && this.tipsRequestFingerprint() !== this.lastTipsFingerprint) {
                    this.scheduleTips();
                }
            }
        },
        recipe: {
            deep: true,
            handler() {
                this.onInputChange();
            }
        },
    },
    created() {
        // Marque tous les appels de la page (autocomplétion, enregistrement…) comme venant du calculateur bêta
        axios.defaults.headers.common['X-Calculateur-Beta'] = '1';
        // Recalcul 800 ms après la dernière saisie
        this.scheduleAutoCalcul = _.debounce(this.autoCalcul, 800);
        this.scheduleTips = _.debounce(this.fetchTips, 400);
    },
    mounted() {
        this._onTipsKeydown = (e) => this.onTipsKeydown(e);
        document.addEventListener('keydown', this._onTipsKeydown);
        axios.get('/group/').then((r) => {
            this.group = r.data.data;
        })
        axios.get('/calculateur-beta/' + this.recipeId).then((r) => {
            this.types = r.data.types
            // Lignes chargées avec la recette : réglages déjà validés (pas d'étiquette « réglages par défaut »)
            this.types.forEach((type) => (type.ingredients || []).forEach((i) => { if (i && i.uniqid) this.$set(this.touchedRows, i.uniqid, true); }));
            if (r.data.recipe.id) {
                this.recipe = r.data.recipe;
                this.resultCalcul = r.data.recipe.calcul;
            }
            // Si la recette ne fixe pas explicitement le choix, pré-remplir avec le réglage du user
            if (this.recipe.rendement === null || this.recipe.rendement === undefined) {
                this.recipe.rendement = r.data.recipe.default_rendement;
            }
            this.optionsCategories = r.data.categories;
            this.optionsSubcategories = r.data.subcategories;
            this.optionsPackageTypes = r.data.packageTypes;
            this.optionsDeliveryModes = r.data.deliveryModes;
            this.optionsPreparations = r.data.preparations || {};
            if (!this.recipe.preparations) {
                this.recipe.preparations = [];
            }
            this.optionsEcobalysePackagings = r.data.ecobalysePackagings || [];
            if (!this.recipe.ecobalyse_packagings || !this.recipe.ecobalyse_packagings.length) {
                this.recipe.ecobalyse_packagings = [{ecobalyse_packaging_id: null, quantity: null}];
            }
            this.optionsTransformations = r.data.transformations || {};
            if (this.recipe.transformation === undefined) {
                this.$set(this.recipe, 'transformation', null);
            }
            // Le chargement initial ne doit pas déclencher de calcul/enregistrement automatique
            this.$nextTick(() => {
                this.lastCalculFingerprint = this.inputFingerprint();
            });
            this.loading = false;
            this.fetchTips();
            // Le calcul enregistré date de l'ancien moteur : le coût environnemental Food 2 est recalculé à l'ouverture (sans enregistrer)
            if (this.recipe.id) {
                this.$nextTick(() => this.calcul({auto: true}));
            }
        }).catch((e) => {
            console.error(e)
            this.loading = false;
        })
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this._onTipsKeydown);
    },
    methods: {
        displayPopupAbo(type) {
            if (type == 'pro') {
                this.$alert('Réservé aux <a target="_blank" href="' + this.urlAboPro + '">abonnements pro</a>', '', {
                    dangerouslyUseHTMLString: true
                });
            } else {
                this.$alert('Réservé aux <a target="_blank" href="' + this.urlLinkAboPro + '">' + this.textLinkAboPro + '</a>', '', {
                    dangerouslyUseHTMLString: true
                });
            }

        },
        handleFileUpload(e) {
            var input = e.target;

            var reader = new FileReader();
            reader.onload = () => {
                var dataURL = reader.result;
                this.recipe.picture_url = dataURL;
            };
            reader.readAsDataURL(input.files[0]);
        },
        setName(v) {
            this.$set(this.recipe, 'name', v);
        },
        retry() {
            this.lastCalculFingerprint = null;
            this.autoStatus = null;
            this.scheduleAutoCalcul();
        },
        goTo(anchor) {
            const el = document.getElementById(anchor);
            if (!el) return;
            el.scrollIntoView({block: 'center', behavior: 'smooth'});
            const input = el.matches('input,select,textarea') ? el : el.querySelector('input,select,textarea');
            if (input) input.focus();
        },
        // Recherche unique (remplace querySearchAsync) ; typeId optionnel
        searchIngredients(q, typeId) {
            // 40 suggestions maximum (comme la maquette) ; q vide autorisé quand un filtre de catégorie est actif
            const params = {q, autocomplete: true, order: 'label', perPage: 40};
            if (typeId) params.type_id = typeId;
            return axios.get('/ingredients', {params}).then((r) => r.data.data);
        },
        addIngredient(item) {
            const typeIndex = this.types.findIndex((t) => t.slug === item.type_slug);
            if (typeIndex === -1) return;
            const type = this.types[typeIndex];
            if (type.ingredients.length >= type.max) return;
            // Quantité par défaut 100 g (spec §2.5) : la ligne est calculable dès l'ajout
            const nextPosition = this.flatIngredients.reduce((m, r) => Math.max(m, r.ingredient.position || 0), -1) + 1;
            const row = {...item, pivot_quantity: 100, position: nextPosition};
            type.ingredients.push(row);
            this.$set(this.touchedRows, row.uniqid, false);
            this.$nextTick(() => this.goTo('qty-' + row.uniqid));
        },
        updateIngredient(row, {key, value, isSetting}) {
            this.$set(row.ingredient, key, value);
            if (isSetting) this.$set(this.touchedRows, row.ingredient.uniqid, true);
        },
        toggleRow(uniqid) {
            this.$set(this.expandedRows, uniqid, !this.expandedRows[uniqid]);
        },
        // Confirmation avant suppression (MessageBox Element UI) : la spec ne l'interdit pas et il n'y a pas d'annulation dans ce lot
        removeIngredient(row) {
            const name = row.ingredient.pivot_label || row.ingredient.label;
            this.$confirm('Retirer « ' + name + ' » de la recette ?', 'Supprimer l\'ingrédient', {
                confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler', type: 'warning', customClass: 'calc-confirm',
            }).then(() => {
                this.types[row.typeIndex].ingredients.splice(row.ingredientIndex, 1);
                this.$delete(this.rowErrors, row.ingredient.uniqid);
            }).catch(() => {});
        },
        // Erreurs nommées : titre, lignes sans quantité
        validate() {
            const errors = [];
            const rowErrors = {};
            if (!this.recipe.name) errors.push({anchor: 'recipe-name', message: 'Le titre de la recette est obligatoire.'});
            this.flatIngredients.forEach((r) => {
                if (!r.ingredient.pivot_quantity) {
                    rowErrors[r.ingredient.uniqid] = 'Quantité manquante';
                    errors.push({anchor: 'qty-' + r.ingredient.uniqid, message: 'Quantité manquante pour « ' + (r.ingredient.pivot_label || r.ingredient.label) + ' ».'});
                }
            });
            this.errors = errors;
            this.rowErrors = rowErrors;
            return errors.length === 0;
        },
        /**
         * Lance le calcul des impacts. options.auto = déclenché par la saisie (pas de popup abonnement).
         * Renvoie true si le résultat a été appliqué, false sinon (erreur, réponse obsolète).
         */
        async calcul(options = {}) {
            const auto = !!(options && options.auto);
            if (this.userRole == 'user') {
                if (!auto) {
                    this.displayPopupAbo()
                }
                return false;
            }
            const seq = ++this.calculSeq;
            const fingerprint = this.inputFingerprint();
            this.autoStatus = 'calcul';
            try {
                const r = await axios.post('/calculateur-beta/calcul', {recipe: this.recipe, types: this.types});
                if (seq !== this.calculSeq) {
                    // Une requête plus récente est partie : on ignore cette réponse
                    return false;
                }
                if (r.data.calcul) {
                    this.applyCalculTypes(r.data.types);
                    this.resultCalcul = r.data.calcul;
                }
                this.lastCalculFingerprint = fingerprint;
                this.autoStatus = 'calculated';
                return true;
            } catch (e) {
                console.error(e);
                if (seq === this.calculSeq) {
                    this.autoStatus = 'error';
                }
                return false;
            }
        },
        /**
         * Applique le retour du calcul ligne par ligne (résultat gCO2e, saison) sans remplacer les objets
         * ingrédients : une saisie en cours pendant la requête n'est pas écrasée.
         */
        applyCalculTypes(serverTypes) {
            (serverTypes || []).forEach((serverType, typeIndex) => {
                const localType = this.types[typeIndex];
                if (!localType || !serverType.ingredients) {
                    return;
                }
                serverType.ingredients.forEach((serverIngredient, ingredientIndex) => {
                    let local = null;
                    if (serverIngredient.uniqid) {
                        local = localType.ingredients.find(i => i && i.uniqid === serverIngredient.uniqid);
                    }
                    if (!local) {
                        local = localType.ingredients[ingredientIndex];
                    }
                    if (!local || local.id !== serverIngredient.id) {
                        return;
                    }
                    this.$set(local, 'result', serverIngredient.result);
                    if (serverIngredient.pivot_saison !== undefined && !local.pivot_saison) {
                        this.$set(local, 'pivot_saison', serverIngredient.pivot_saison);
                    }
                });
            });
        },
        /**
         * Empreinte des données d'entrée du calcul (recette sans champs volatils + pivots des ingrédients),
         * valeurs normalisées pour ignorer les différences de type renvoyées par le serveur.
         */
        inputFingerprint() {
            const norm = (v) => {
                if (v === null || v === undefined || v === '') return '';
                if (typeof v === 'boolean') return v ? '1' : '0';
                if (typeof v === 'number') return String(v);
                if (Array.isArray(v)) return v.map(norm);
                if (typeof v === 'object') {
                    const out = {};
                    Object.keys(v).sort().forEach((k) => { out[k] = norm(v[k]); });
                    return out;
                }
                return String(v);
            };
            const recipeKeys = ['category_id', 'subcategories_ids', 'nb_couvert', 'rendement', 'package', 'packages',
                'ecobalyse_packagings', 'delivery', 'deliveries', 'preparations', 'transformation', 'scope_recipe',
                'preparation_minutes', 'cuisson_minutes', 'facilite', 'budget', 'name', 'text', 'video'];
            const recipe = {};
            recipeKeys.forEach((k) => { recipe[k] = norm(this.recipe[k]); });
            const ingredients = this.types.map((type) => (type.ingredients || []).map((ingredient) => {
                if (!ingredient || !ingredient.id) return null;
                const pivot = {id: ingredient.id};
                Object.keys(ingredient).forEach((k) => {
                    if (k.indexOf('pivot_') === 0 && k.indexOf('options_') !== 0) {
                        pivot[k] = norm(ingredient[k]);
                    }
                });
                return pivot;
            }));
            return JSON.stringify({recipe, ingredients});
        },
        hasCalculableIngredient() {
            return this.types.some(type => (type.ingredients || []).some(i => i && i.id && i.pivot_quantity));
        },
        onInputChange() {
            if (!this.autoCalculAvailable || !this.scheduleAutoCalcul) {
                return;
            }
            this.scheduleAutoCalcul();
        },
        /** Calcul automatique (debouncé) puis enregistrement silencieux si la recette existe déjà. */
        async autoCalcul() {
            if (!this.autoCalculAvailable) {
                return;
            }
            // Signalement en place des quantités manquantes, avant tout retour anticipé et sans bloquer le calcul
            this.validate();
            if (!this.hasCalculableIngredient()) {
                return;
            }
            if (this.inputFingerprint() === this.lastCalculFingerprint) {
                return;
            }
            if (this.autoRunning || this.loadingSave) {
                this.autoPending = true;
                return;
            }
            this.autoRunning = true;
            try {
                const calculated = await this.calcul({auto: true});
                if (calculated && this.recipe.name && (this.recipe.id || this.hasCalculableIngredient())) {
                    await this.autoSave();
                }
            } finally {
                this.autoRunning = false;
                if (this.autoPending) {
                    this.autoPending = false;
                    this.scheduleAutoCalcul();
                }
            }
        },
        async autoSave() {
            this.autoStatus = 'save';
            try {
                await this.persist({silent: true});
                const now = new Date();
                this.autoSavedAt = ('0' + now.getHours()).slice(-2) + ':' + ('0' + now.getMinutes()).slice(-2);
                this.autoStatus = 'saved';
            } catch (e) {
                console.error(e);
                this.autoStatus = 'error';
            }
        },
        // Calcul unitaire d'une ligne, temporisé par ligne (600 ms après le dernier changement) :
        // les flèches du champ numérique déclenchent un `change` à chaque clic.
        scheduleGco2e(row) {
            const key = row.ingredient.uniqid || (row.typeIndex + ':' + row.ingredientIndex);
            this._gco2eTimers = this._gco2eTimers || {};
            clearTimeout(this._gco2eTimers[key]);
            this._gco2eTimers[key] = setTimeout(() => {
                delete this._gco2eTimers[key];
                this.calculGco2e(row.ingredient, row.typeIndex, row.ingredientIndex);
            }, 600);
        },
        // ---- Pistes d'amélioration (ticket 24, phase 1) ----
        tipLines() {
            return this.flatIngredients.map((r) => {
                const i = r.ingredient;
                return {
                    uniqid: i.uniqid, id: i.id, pivot_quantity: i.pivot_quantity, pivot_quantity_unit: i.pivot_quantity_unit,
                    pivot_provenance: i.pivot_provenance, pivot_mode_conserv: i.pivot_mode_conserv, pivot_type_label: i.pivot_type_label,
                    pivot_label: i.pivot_label, result: i.result, position: i.position,
                };
            });
        },
        // Empreinte sans le gCO2e, qui change seul après un recalcul de ligne
        tipsFingerprint() {
            return JSON.stringify(this.tipLines().map((l) => Object.assign({}, l, {result: null})));
        },
        // Empreinte des lignes envoyées à /calculateur/pistes ; gCO2e arrondi à l'entier pour ignorer le bruit
        // flottant (même valeur affichée) sans ignorer un vrai changement de résultat
        tipsRequestFingerprint() {
            return JSON.stringify(this.tipLines().map((l) => Object.assign({}, l, {result: Math.round(parseFloat(l.result) || 0)})));
        },
        fetchTips() {
            // Mémorisée avant l'envoi (y compris si la liste est vide) : le watcher ne relance pas une empreinte déjà envoyée
            this.lastTipsFingerprint = this.tipsRequestFingerprint();
            const lines = this.tipLines();
            if (!lines.length) {
                this.tips = [];
                return;
            }
            const seq = ++this.tipsRequestSeq;
            axios.post('/calculateur/pistes', {lines}).then((r) => {
                if (seq !== this.tipsRequestSeq) return; // réponse dépassée par une requête plus récente : ignorée
                this.tips = r.data.tips || [];
            }).catch(() => {
                // Échec (dont 429) : on garde la liste précédente plutôt que de la vider
            });
        },
        // Une modification manuelle après une piste vide l'historique : l'annulation ne défait jamais le travail de l'utilisateur
        checkTipsHistory() {
            if (this.tipsHistory.length && this.tipsFingerprint() !== this.tipsBaseline) {
                this.tipsHistory = [];
                this.appliedTipIds = [];
            }
        },
        async applyTip(tip) {
            if (!this.autoCalculAvailable) {
                this.displayPopupAbo();
                return;
            }
            if (this.applyingTip) return;
            this.applyingTip = true;
            try {
                const r = await axios.post('/calculateur/pistes/' + tip.id + '/apply', {lines: this.tipLines()});
                const ops = r.data || {};
                const isEmpty = !(ops.updates && ops.updates.length) && !(ops.removals && ops.removals.length) && !(ops.additions && ops.additions.length);
                if (isEmpty) {
                    // Piste devenue obsolète entre l'affichage et le clic (recette modifiée entretemps) : rien à empiler/marquer
                    this.fetchTips();
                    this.$message({type: 'info', message: "Cette piste ne s'applique plus à la recette."});
                    return;
                }
                this.tipsHistory.push(this.types.map((t) => JSON.parse(JSON.stringify(t.ingredients || []))));
                this.appliedTipIds.push(tip.id);
                this.applyTipOps(ops);
                this.tipsBaseline = this.tipsFingerprint();
                this.fetchTips();
            } catch (e) {
                console.error(e);
                this.$message({type: 'error', message: "La piste n'a pas pu être appliquée."});
            } finally {
                this.applyingTip = false;
            }
        },
        // Rejoue les opérations du serveur : retraits, ajouts (dans le groupe de leur type), mises à jour
        applyTipOps(ops) {
            const removed = ops.removals || [];
            this.types.forEach((t) => {
                t.ingredients = (t.ingredients || []).filter((i) => removed.indexOf(i.uniqid) === -1);
            });
            const touched = [];
            (ops.additions || []).forEach((line) => {
                const type = this.types.find((t) => t.slug === line.type_slug);
                if (!type) return;
                type.ingredients.push(line);
                this.$set(this.touchedRows, line.uniqid, true);
                touched.push(line.uniqid);
            });
            (ops.updates || []).forEach((u) => {
                const row = this.flatIngredients.find((r) => r.ingredient.uniqid === u.uniqid);
                if (!row) return;
                Object.keys(u.changes).forEach((k) => this.$set(row.ingredient, k, u.changes[k]));
                touched.push(u.uniqid);
            });
            // gCO2e des lignes changées ; le calcul complet part via le watcher `types`
            this.$nextTick(() => this.flatIngredients
                .filter((r) => touched.indexOf(r.ingredient.uniqid) !== -1)
                .forEach((r) => this.scheduleGco2e(r)));
        },
        restoreTips(snapshot) {
            // Les gCO2e en attente ou en vol visent l'état qu'on annule : minuteries vidées, réponses tardives ignorées
            Object.keys(this._gco2eTimers || {}).forEach((key) => clearTimeout(this._gco2eTimers[key]));
            this._gco2eTimers = {};
            this._gco2eGen = (this._gco2eGen || 0) + 1;
            snapshot.forEach((ingredients, k) => {
                if (this.types[k]) this.types[k].ingredients = ingredients;
            });
            this.tipsBaseline = this.tipsFingerprint();
        },
        undoTip() {
            if (this.applyingTip) return;
            if (!this.tipsHistory.length) return;
            this.restoreTips(this.tipsHistory.pop());
            this.appliedTipIds.pop();
        },
        resetTips() {
            if (this.applyingTip) return;
            if (!this.tipsHistory.length) return;
            const first = this.tipsHistory[0];
            this.tipsHistory = [];
            this.appliedTipIds = [];
            this.restoreTips(first);
        },
        // Ctrl+Z (ou Cmd+Z) = annuler la dernière piste, sauf quand le curseur est dans un champ de saisie
        onTipsKeydown(e) {
            if (this.applyingTip) return;
            if (!(e.ctrlKey || e.metaKey) || e.shiftKey || (e.key || '').toLowerCase() !== 'z' || !this.tipsHistory.length) return;
            const el = e.target;
            if (el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].indexOf(el.tagName) !== -1)) return;
            e.preventDefault();
            this.undoTip();
        },
        calculGco2e(ingredient, typeKey, ingredientKey) {
            if (!ingredient['pivot_quantity']) {
                return;
            }
            if (this.userRole == 'user') {
                return;
            }
            // Retrouvée par uniqid à la réponse (au lieu de typeKey/ingredientKey, plus valides si une piste a
            // retiré/ajouté des lignes entretemps) ; ignorée si la ligne n'existe plus.
            const uniqid = ingredient.uniqid;
            const gen = this._gco2eGen || 0;
            axios.post('/calculateur-gco2e', {recipe: this.recipe, ingredient}).then((r) => {
                if ((this._gco2eGen || 0) !== gen) return;
                let row = null;
                this.types.some((t) => {
                    row = (t.ingredients || []).find((i) => i.uniqid === uniqid);
                    return !!row;
                });
                if (row) row.result = r.data.result;
            })
        },
        async save() {
            if (this.userRole == 'user') {
                this.displayPopupAbo()
                return;
            }

            if (!this.validate()) return;

            var calculed = await this.calcul();

            if (calculed === false) {
                return
            }

            this.loadingSave = true
            try {
                await this.persist({silent: false});
                const now = new Date();
                this.autoSavedAt = ('0' + now.getHours()).slice(-2) + ':' + ('0' + now.getMinutes()).slice(-2);
                this.autoStatus = 'saved';
                this.$notify({
                    title: 'Succès',
                    message: 'Recette bien enregistrée.',
                    type: 'success'
                });
            } catch (e) {
                console.error(e);
                this.$alert('Une erreur est survenue, merci de ressayer plus tard.', 'Erreur', {
                    confirmButtonText: 'Fermer'
                });
            } finally {
                this.loadingSave = false
                if (this.autoPending) {
                    this.autoPending = false;
                    this.scheduleAutoCalcul();
                }
            }
        },
        /**
         * Enregistre la recette (création ou mise à jour).
         * silent = enregistrement automatique : la recette locale n'est pas remplacée par la réponse
         * (pour ne pas écraser une saisie en cours).
         * La photo part toujours à part, en multipart, une fois l'identifiant connu.
         */
        async persist(options = {}) {
            const silent = !!(options && options.silent);
            let ingredients = this.types.map((v) => v.ingredients).flat().filter(v => v && v.id);
            // picture_url peut contenir l'aperçu base64 local : jamais envoyé dans le corps JSON
            const recipePayload = Object.assign({}, this.recipe);
            delete recipePayload.picture_url;
            if (this.recipe.id) {
                const r = await axios.post('/recipes/' + this.recipe.id, {recipe: recipePayload, ingredients: ingredients});
                if (!silent) {
                    this.recipe = r.data.data;
                    this.$nextTick(() => {
                        this.lastCalculFingerprint = this.inputFingerprint();
                    });
                }
                await this.uploadPicture();
            } else {
                const r = await axios.post('/recipes', {recipe: recipePayload, ingredients: ingredients});
                if (silent) {
                    // Création par l'autosave : ne pas écraser la saisie en cours, ne garder que l'identité
                    this.$set(this.recipe, 'id', r.data.data.id);
                    this.$set(this.recipe, 'slug', r.data.data.slug);
                } else {
                    this.recipe = r.data.data;
                }
                await this.uploadPicture();
                this.$nextTick(() => {
                    this.lastCalculFingerprint = this.inputFingerprint();
                });
                window.history.pushState({}, r.data.data.name, '/calculateur-beta/' + r.data.data.slug)
            }
        },
        /**
         * Envoie la photo choisie, une fois l'identifiant de recette connu.
         * Ne remplace jamais la recette locale (saisie en cours) : seule l'URL de la photo est reprise.
         * Renvoie une promesse pour que persist() puisse l'attendre.
         */
        uploadPicture() {
            if (!this.$refs.picture || !this.$refs.picture.files.length) {
                return Promise.resolve();
            }
            let formData = new FormData();
            formData.append('picture', this.$refs.picture.files[0]);
            return axios.post('/recipes/' + this.recipe.id, formData, {headers: {'Content-Type': 'multipart/form-data'}}).then((r) => {
                this.$set(this.recipe, 'picture_url', r.data.data.picture_url);
                // Vider l'input : la même photo ne doit pas repartir à chaque enregistrement automatique
                this.$refs.picture.value = '';
            });
        },
        openDialogTransform() {
            if (this.userRole == 'user' || this.userRole == 'pro') {
                this.displayPopupAbo('pro')
                return;
            }
            axios.get('/recipes/' + this.recipe.id + '/checkTransform').then((r) => {
                this.transformStatus = r.data;
                this.dialogueTransform = true
            })
        },
        closedModalDialog() {
            this.recipeToIngredient.type_id = null
            this.transformStatus.already_exists = false
        },
        transformToIngredient(mode) {
            this.loadingTransform = true
            axios.post('/recipes/' + this.recipe.id + '/transform', {mode, ...this.recipeToIngredient}).then((r) => {
                this.recipe = r.data.data;
                this.dialogueTransform = false;
            }).catch(() => {

            }).then(() => {
                this.loadingTransform = false;
            })
        },
        choiceFile() {
            document.querySelector('#picture').click()
        },
    }
}

</script>
